/**
 * React Query hooks for the Faire-Sage shipment pushback UI (Task 10).
 *
 * - useShipments(filters)    GET {API_BASE}/v1/commerce/shipments
 * - useShipmentCounts()      GET {API_BASE}/v1/commerce/shipments/counts
 * - useShipmentPush()        POST .../shipments/{faireOrderId}/push (admin)
 * - useShipmentBulkPush()    POST .../shipments/bulk-push           (admin)
 *
 * All requests carry credentials via the shared apiFetch helper.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';
import { FAIRE_API_BASE } from './useFaireRuns';

export type ShipmentPushStatus = 'eligible' | 'pushed' | 'failed' | 'suppressed';

export type ShipmentRow = {
  faire_order_id: string;
  sage_so_number: string;
  sage_invoice_no: string;
  tracking_number: string | null;
  carrier: string | null;
  freight_amount: string | number | null;
  ship_date: string | null;
  first_eligible_at: string;
  pushed_at: string | null;
  pushed_by: string | null;
  push_status: ShipmentPushStatus;
  faire_response_code: number | null;
  faire_response_body: string | null;
  retry_count: number;
  retailer_name: string | null;
};

export type ShipmentsResponse = {
  shipments: ShipmentRow[];
  limit: number;
  offset: number;
  status: string;
  q: string;
  since: string;
  until: string;
};

export type ShipmentCounts = {
  // Shipment pushback counts
  eligible: number;
  pushed_today: number;
  failed_today: number;
  // Order state counts (added 2026-05-24 for the "Orders awaiting
  // processing" tile family on the Faire dashboard).
  awaiting_customer_mapping: number;
  ready_to_process: number;
  processed_today: number;
  total_unprocessed: number;
};

export type ShipmentsFilters = {
  status?: 'eligible' | 'pushed' | 'failed' | 'suppressed' | 'all';
  limit?: number;
  offset?: number;
  q?: string;
  since?: string;
  until?: string;
};

function buildShipmentsQuery(filters: ShipmentsFilters): string {
  const params = new URLSearchParams();
  params.set('status', filters.status ?? 'eligible');
  params.set('limit', String(filters.limit ?? 100));
  params.set('offset', String(filters.offset ?? 0));
  if (filters.q) params.set('q', filters.q);
  if (filters.since) params.set('since', filters.since);
  if (filters.until) params.set('until', filters.until);
  return params.toString();
}

export type UseShipmentsResult = {
  shipments: ShipmentRow[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<ShipmentsResponse, Error>;
};

export function useShipments(filters: ShipmentsFilters = {}): UseShipmentsResult {
  const qs = buildShipmentsQuery(filters);
  const query = useQuery<ShipmentsResponse, Error>({
    queryKey: ['faire-shipments', filters],
    queryFn: () => apiGet<ShipmentsResponse>(`${FAIRE_API_BASE}/shipments?${qs}`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
  return {
    shipments: query.data?.shipments ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

export type UseShipmentCountsResult = {
  counts: ShipmentCounts;
  isLoading: boolean;
  error: Error | null;
};

export function useShipmentCounts(): UseShipmentCountsResult {
  const query = useQuery<ShipmentCounts, Error>({
    queryKey: ['faire-shipment-counts'],
    queryFn: () => apiGet<ShipmentCounts>(`${FAIRE_API_BASE}/shipments/counts`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 20_000,
  });
  return {
    counts: query.data ?? {
      eligible: 0,
      pushed_today: 0,
      failed_today: 0,
      awaiting_customer_mapping: 0,
      ready_to_process: 0,
      processed_today: 0,
      total_unprocessed: 0,
    },
    isLoading: query.isLoading,
    error: query.error,
  };
}

export type ShipmentPushResponse = {
  faire_order_id: string;
  ok: boolean;
  status_code?: number;
  push_status: string;
  message?: string;
};

export function useShipmentPush() {
  const qc = useQueryClient();
  return useMutation<ShipmentPushResponse, Error, string>({
    mutationFn: (faireOrderId: string) =>
      apiPost<ShipmentPushResponse>(
        `${FAIRE_API_BASE}/shipments/${encodeURIComponent(faireOrderId)}/push`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['faire-shipments'] });
      void qc.invalidateQueries({ queryKey: ['faire-shipment-counts'] });
    },
  });
}

export type ShipmentBulkPushResponse = {
  pushed: number;
  total: number;
  results: ShipmentPushResponse[];
};

export function useShipmentBulkPush() {
  const qc = useQueryClient();
  return useMutation<ShipmentBulkPushResponse, Error, string[]>({
    mutationFn: (orderIds: string[]) =>
      apiPost<ShipmentBulkPushResponse>(`${FAIRE_API_BASE}/shipments/bulk-push`, {
        order_ids: orderIds,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['faire-shipments'] });
      void qc.invalidateQueries({ queryKey: ['faire-shipment-counts'] });
    },
  });
}

export function formatFreight(v: string | number | null): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}
